/**
 * Auth0 Callback Page
 *
 * Handles the OAuth callback from Auth0 after authentication.
 * Verifies user with FastAPI backend (which auto-provisions if needed)
 * and redirects to dashboard.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Loader2 } from 'lucide-react';
import { config } from '@/utils/env';
import { popReturnTo } from '@/hooks/auth/useDeepLinkReturnTo';
import { resolvePortalHome } from '@/hooks/portal/usePortalHome';

/**
 * sessionStorage key marking that we already auto-restarted login once to
 * recover from an "Invalid state" callback. Bounds recovery to a single retry
 * so a genuinely persistent failure surfaces an error instead of looping.
 */
const RECOVERY_FLAG_KEY = 'curaition_auth_recovery_attempted';

/**
 * auth0-spa-js throws "Invalid state" from its CSRF/replay guard when the
 * callback lands without the matching in-flight login transaction — a stale,
 * replayed, or cross-tab callback (sessionStorage is per-tab). These two codes
 * are recoverable: minting a fresh transaction via a clean login restart fixes
 * them. Anything else (access_denied, login_required, …) is a real failure.
 */
const RECOVERABLE_AUTH_ERROR_CODES = new Set(['missing_transaction', 'state_mismatch']);

function isRecoverableAuthError(error: Error | null | undefined): boolean {
  if (!error) return false;
  const code = (error as { error?: string }).error;
  if (code && RECOVERABLE_AUTH_ERROR_CODES.has(code)) return true;
  // Defensive fallback: older SDK builds surface only the message.
  return error.message === 'Invalid state';
}

/**
 * Records a recovery attempt and reports whether one may proceed now. Returns
 * false if recovery was already attempted this session, OR if sessionStorage is
 * unusable (private mode / partitioned) — in which case a retry would risk an
 * infinite redirect loop, so we'd rather surface the error.
 */
function claimRecoveryAttempt(): boolean {
  try {
    if (sessionStorage.getItem(RECOVERY_FLAG_KEY)) return false;
    sessionStorage.setItem(RECOVERY_FLAG_KEY, '1');
    // Verify the write actually persisted before trusting it as a loop guard.
    return sessionStorage.getItem(RECOVERY_FLAG_KEY) === '1';
  } catch {
    return false;
  }
}

function clearRecoveryFlag(): void {
  try {
    sessionStorage.removeItem(RECOVERY_FLAG_KEY);
  } catch {
    // sessionStorage unavailable — nothing to clear.
  }
}

export function CallbackPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, error, getAccessTokenSilently, user, loginWithRedirect } =
    useAuth0();
  const [provisionError, setProvisionError] = useState<string | null>(null);
  const [isProvisioning, setIsProvisioning] = useState(false);
  // True while we auto-restart login to recover from a stale "Invalid state"
  // callback — show the spinner, not the terminal error, during the redirect.
  const [isRecovering, setIsRecovering] = useState(false);

  useEffect(() => {
    const verifyAndProvisionUser = async () => {
      if (isLoading) return;

      if (error) {
        // "Invalid state" (missing/mismatched transaction) is a recoverable
        // stale-callback condition, not a dead end. Restart login once to mint
        // a fresh transaction; the captured returnTo (sessionStorage) survives.
        if (isRecoverableAuthError(error) && claimRecoveryAttempt()) {
          setIsRecovering(true);
          await loginWithRedirect({ authorizationParams: { screen_hint: 'login' } });
          return;
        }
        // Non-recoverable, or we already retried once — surface it.
        clearRecoveryFlag();
        console.error('Auth0 error:', error);
        setProvisionError(error.message);
        return;
      }

      if (!isAuthenticated || !user) {
        // Not authenticated yet, redirect to sign in
        navigate('/signin', { replace: true });
        return;
      }

      // Authentication succeeded — reset the one-shot recovery guard so a
      // future stale callback can self-heal again.
      clearRecoveryFlag();

      // User is authenticated, verify/provision them via FastAPI backend
      // The FastAPI dual-mode auth will auto-provision the user if they don't exist
      setIsProvisioning(true);

      try {
        const accessToken = await getAccessTokenSilently();

        // Call FastAPI /auth/me endpoint - this auto-provisions users via dual-mode auth
        const response = await fetch(`${config.apiUrl}/auth/me`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || `Authentication failed: ${response.status}`);
        }

        const userData = await response.json();

        // Fetch user permissions to determine role
        let userRole = 'user';
        try {
          const permissionsResponse = await fetch(`${config.apiUrl}/auth/me/permissions`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
          });

          if (permissionsResponse.ok) {
            const permissions = await permissionsResponse.json();
            // Check for super_admin role on ANY domain
            // (Backend expands super_admin with null domain to all domains with super_admin role)
            const isSuperAdmin = permissions.some((p: { role: string }) =>
              p.role === 'super_admin'
            );
            if (isSuperAdmin) {
              userRole = 'super_admin';
            } else if (permissions.some((p: { role: string }) => p.role === 'domain_admin')) {
              userRole = 'admin';
            }
          }
        } catch (permErr) {
          console.warn('Failed to fetch permissions, defaulting to user role:', permErr);
        }

        // Transform backend response and store in localStorage
        // Prefer Auth0 email (from ID token) over backend placeholder
        const backendEmail = userData.email || '';
        const resolvedEmail = backendEmail.includes('@oauth.curaition.xyz')
          ? (user.email || backendEmail)
          : backendEmail;

        const userForStorage = {
          id: userData.id,
          emailAddress: resolvedEmail,
          firstName: userData.first_name || user.given_name || '',
          lastName: userData.last_name || user.family_name || '',
          displayName: userData.display_name || user.name || '',
          role: userRole,
          active: userData.active ?? true,
          createdAt: userData.created_at || new Date().toISOString(),
          organizationId: userData.organization_id || undefined,
          organizationRole: userData.organization_role || undefined,
          portalMemberExperience: userData.portal_member_experience || undefined,
        };

        localStorage.setItem('curaition_user', JSON.stringify(userForStorage));

        // Deep-link survival: if a returnTo was captured before the
        // Auth0 round-trip (ProtectedRoute or AcceptInvitePage path),
        // honour it over the default role-based redirect. popReturnTo
        // clears the value so refreshes don't re-trigger.
        const deepLinkReturnTo = popReturnTo();
        if (deepLinkReturnTo) {
          navigate(deepLinkReturnTo, { replace: true });
          return;
        }

        // Role-based routing: admins → admin dashboard, org members → portal
        if (userRole === 'super_admin' || userRole === 'admin') {
          navigate('/dashboard', { replace: true });
        } else if (userData.organization_id) {
          // Check if onboarding is complete before routing to dashboard.
          // Without this, a page refresh after Step 1 (org created) but before
          // Steps 2-3 (domains, complete) would skip onboarding entirely.
          try {
            const onboardingRes = await fetch(`${config.apiUrl}/portal/onboarding/status`, {
              headers: { 'Authorization': `Bearer ${accessToken}` },
            });
            if (onboardingRes.ok) {
              const onboardingData = await onboardingRes.json();
              if (onboardingData.step !== 'done') {
                navigate('/portal/onboarding', { replace: true });
                return;
              }
            }
          } catch {
            // Fall through to dashboard on network error — PortalLayout guard
            // will catch incomplete onboarding as a defense-in-depth measure.
          }
          const isOrgAdmin = userData.organization_role === 'owner' || userData.organization_role === 'admin';
          navigate(resolvePortalHome(userData.portal_member_experience, isOrgAdmin), { replace: true });
        } else {
          navigate('/portal/onboarding', { replace: true });
        }
      } catch (err) {
        console.error('User verification error:', err);
        setProvisionError(err instanceof Error ? err.message : 'Failed to verify user');
      } finally {
        setIsProvisioning(false);
      }
    };

    verifyAndProvisionUser();
  }, [isAuthenticated, isLoading, error, user, getAccessTokenSilently, navigate, loginWithRedirect]);

  if ((error || provisionError) && !isRecovering) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        backgroundColor: '#ffffff'
      }}>
        <div style={{
          width: '100%',
          maxWidth: '400px',
          textAlign: 'center'
        }}>
          <div style={{
            padding: '2rem',
            border: '1px solid #fecaca',
            borderRadius: '0.5rem',
            backgroundColor: '#fef2f2'
          }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem', color: '#dc2626' }}>
              Authentication Error
            </h2>
            <p style={{ color: '#6b7280', fontSize: '0.8125rem', marginBottom: '1.5rem' }}>
              {error?.message || provisionError}
            </p>
            <button
              type="button"
              onClick={() => navigate('/signin', { replace: true })}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              Back to Sign In
            </button>
            <p style={{ color: '#6b7280', fontSize: '0.8125rem', marginTop: '1.5rem' }}>
              Still having trouble?{' '}
              <a href="mailto:support@curaition.xyz" style={{ color: '#3b82f6' }}>
                Contact support@curaition.xyz
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem',
      backgroundColor: '#ffffff'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '400px',
        textAlign: 'center'
      }}>
        <Loader2
          style={{
            width: '3rem',
            height: '3rem',
            margin: '0 auto 1rem',
            animation: 'spin 1s linear infinite',
            color: '#3b82f6'
          }}
        />
        <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
          {isProvisioning ? 'Setting up your account...' : 'Completing sign in...'}
        </h2>
        <p style={{ color: '#666' }}>
          Please wait while we authenticate you.
        </p>
      </div>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
